import { useCallback, useEffect, useState } from 'react';
import { PLACY_LOGO_SRC } from './OffizzBrandMark';
import { AssetImageThumb } from './AssetImageThumb';
import { useBookingListAssetThumbs } from '../hooks/useBookingListAssetThumbs';
import { useI18n } from '../i18n';
import { useAppNavigation } from '../navigation/AppNavigationContext';
import {
  bookingRecordAssetName,
  bookingRecordDateRangeLabel,
  bookingRowThumbSrc,
  formatBookingStartsInLabel,
} from '../utils/bookingListDisplay';
import { bookingRecordPrimaryId } from '../utils/bookingRecordMutate';
import styles from './NotificationsPanel.module.css';

type NotificationsPanelProps = {
  onClose: () => void;
  /** When false, show a sign-in prompt instead of fetching bookings. */
  canLoadBookings: boolean;
  upcoming: Record<string, unknown>[];
  loadState: 'idle' | 'loading' | 'ready' | 'error';
  loadError: string | null;
  onRefresh: () => void | Promise<void>;
};

function stableBookingKey(row: Record<string, unknown>, index: number): string {
  return bookingRecordPrimaryId(row) ?? `idx-${index}`;
}

export function NotificationsPanel({
  onClose,
  canLoadBookings,
  upcoming,
  loadState,
  loadError,
  onRefresh,
}: NotificationsPanelProps) {
  const { t, language } = useI18n();
  const { openBookings } = useAppNavigation();
  const dir = language === 'he' ? 'rtl' : 'ltr';

  useEffect(() => {
    if (canLoadBookings) {
      void onRefresh();
    }
  }, [canLoadBookings, onRefresh]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const onBackdropPointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (event.target === event.currentTarget) {
        onClose();
      }
    },
    [onClose],
  );

  const onOpenBookings = useCallback(() => {
    onClose();
    openBookings();
  }, [onClose, openBookings]);

  const onSelectBooking = useCallback(
    (row: Record<string, unknown>) => {
      const id = bookingRecordPrimaryId(row);
      if (!id) {
        return;
      }
      onClose();
      openBookings({ bookingId: id });
    },
    [onClose, openBookings],
  );

  let statusMessage: string | null = null;
  const showEmptyState = canLoadBookings && loadState === 'ready' && upcoming.length === 0;
  if (!canLoadBookings) {
    statusMessage = t('header.notificationsSignIn');
  } else if (loadState === 'loading') {
    statusMessage = t('header.notificationsLoading');
  } else if (loadState === 'error' && loadError) {
    statusMessage = loadError;
  }

  return (
    <div
      className={styles.overlay}
      role="dialog"
      aria-modal="true"
      aria-label={t('header.notificationsTitle')}
      dir={dir}
      onPointerDown={onBackdropPointerDown}
    >
      <div className={styles.sheet}>
        <div className={styles.headerRow}>
          <h2 className={styles.title}>{t('header.notificationsTitle')}</h2>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label={t('common.close')}
          >
            ✕
          </button>
        </div>
        {canLoadBookings && !showEmptyState ? (
          <p className={styles.subtitle}>{t('header.notificationsSubtitle')}</p>
        ) : null}

        {statusMessage ? (
          <p
            className={loadState === 'error' ? styles.statusError : styles.status}
            role={loadState === 'error' ? 'alert' : 'status'}
          >
            {statusMessage}
          </p>
        ) : null}

        {showEmptyState ? (
          <div className={styles.emptyState} role="status">
            <img
              src={PLACY_LOGO_SRC}
              alt={t('splash.brandAlt')}
              className={styles.emptyLogo}
              draggable={false}
            />
            <p className={styles.emptyMessage}>{t('header.notificationsEmpty')}</p>
          </div>
        ) : null}

        {canLoadBookings && loadState === 'ready' && upcoming.length > 0 ? (
          <div className={styles.results}>
            <NotificationBookingList bookings={upcoming} onSelect={onSelectBooking} />
          </div>
        ) : null}

        {canLoadBookings && loadState === 'ready' && upcoming.length > 0 ? (
          <button type="button" className={styles.viewAllButton} onClick={onOpenBookings}>
            {t('header.notificationsViewAll')}
          </button>
        ) : null}
      </div>
    </div>
  );
}

function NotificationBookingList({
  bookings,
  onSelect,
}: {
  bookings: Record<string, unknown>[];
  onSelect: (row: Record<string, unknown>) => void;
}) {
  const { language } = useI18n();
  const thumbByAssetId = useBookingListAssetThumbs(bookings);

  return (
    <ul className={styles.list}>
      {bookings.map((row, index) => {
        const assetName = bookingRecordAssetName(row);
        const schedule = bookingRecordDateRangeLabel(row, language);
        const startsIn = formatBookingStartsInLabel(row, language);
        const thumbSrc = bookingRowThumbSrc(row, thumbByAssetId);
        return (
          <li key={stableBookingKey(row, index)} className={styles.item}>
            <button type="button" className={styles.rowButton} onClick={() => onSelect(row)}>
              <span className={styles.thumbWrap}>
                <AssetImageThumb src={thumbSrc} className={styles.thumb} />
              </span>
              <span className={styles.rowMain}>
                <span className={styles.name}>{assetName}</span>
                <span className={styles.schedule}>{schedule}</span>
                {startsIn ? <span className={styles.startsIn}>{startsIn}</span> : null}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
